// Hole contests — closest to the pin, longest drive, and the rest of the signs on the
// tee boxes at an outing. Pure, no imports outside domain types.
//
// FORMAT-AGNOSTIC BY CONSTRUCTION. A contest is declared on the ROUND, not inside a game's
// config, and its winner is a `playerId`, not a team. So the same code serves a four-person
// scramble, an individual member-guest, and a Tuesday foursome playing for nothing — the
// scoring format never enters into it.
//
// That is the same property that decided how a scramble is stored: real players stay on the
// card, so a closest-to-the-pin always has a person to belong to. A team modelled as one
// pseudo-player would have had nobody to award it to.

import type { ContestKind, ContestResult, HoleContest, Round } from '../types';

export const CONTEST_LABELS: Record<ContestKind, string> = {
  ctp: 'Closest to the pin',
  longDrive: 'Longest drive',
  longPutt: 'Longest putt',
  straightestDrive: 'Straightest drive',
};

/** For a hole chip or a cart sign, where there is no room for the full name. */
export const CONTEST_SHORT: Record<ContestKind, string> = {
  ctp: 'CTP',
  longDrive: 'LD',
  longPutt: 'LP',
  straightestDrive: 'SD',
};

/** What to show: the host's own wording if they gave one, else the standard name. */
export function contestLabel(contest: HoleContest): string {
  return contest.label?.trim() || CONTEST_LABELS[contest.kind];
}

/** Contests declared on a hole, in declaration order. */
export function contestsOnHole(round: Round, holeIdx: number): HoleContest[] {
  return round.contests?.[holeIdx] ?? [];
}

/**
 * Every hole carrying a contest, in hole order.
 *
 * This is what answers "which holes have something on them" before anyone tees off — the
 * thing a player actually wants to know walking to the first tee.
 */
export function holesWithContests(round: Round): number[] {
  const contests = round.contests;
  if (!contests) return [];
  return Object.keys(contests)
    .map(Number)
    .filter((idx) => Number.isInteger(idx) && (contests[idx]?.length ?? 0) > 0)
    .sort((a, b) => a - b);
}

/** True when this player is allowed to win this contest. */
export function isEligible(contest: HoleContest, playerId: string): boolean {
  const only = contest.eligiblePlayerIds;
  if (!only || only.length === 0) return true;
  return only.includes(playerId);
}

/** The recorded winner of one contest on one hole, or null if nobody has taken it yet. */
export function contestWinner(
  round: Round,
  holeIdx: number,
  kind: ContestKind
): ContestResult | null {
  return round.holes[holeIdx]?.contestResults?.find((r) => r.kind === kind) ?? null;
}

export type ContestStanding = {
  holeIdx: number;
  /** 1-based, already offset for a back nine by the caller passing holeNumberOffset. */
  holeNumber: number;
  contest: HoleContest;
  label: string;
  /** Null until somebody has been recorded as taking it. */
  result: ContestResult | null;
};

/**
 * Every contest in a round with its winner so far — the prize table, in order.
 *
 * Unclaimed contests are included with a null result on purpose: the host needs to see what
 * is still outstanding at the end of the day, not a list that quietly omits it.
 */
export function contestStandings(round: Round, holeNumberOffset = 0): ContestStanding[] {
  const out: ContestStanding[] = [];
  for (const holeIdx of holesWithContests(round)) {
    for (const contest of contestsOnHole(round, holeIdx)) {
      out.push({
        holeIdx,
        holeNumber: holeIdx + 1 + holeNumberOffset,
        contest,
        label: contestLabel(contest),
        result: contestWinner(round, holeIdx, contest.kind),
      });
    }
  }
  return out;
}

/**
 * Contest results across every round of an event, since a tournament's groups each score
 * their own card and the prize table is assembled from all of them.
 *
 * A contest belongs to a HOLE, and every group plays that hole, so the same contest appears
 * on many cards with different claimants. Only one can win it — the host settles that from
 * the measurements — so this reports every claim rather than silently picking one.
 */
export function eventContestClaims(
  rounds: Round[],
  holeNumberOffset = 0
): Array<{
  holeIdx: number;
  holeNumber: number;
  kind: ContestKind;
  label: string;
  claims: Array<{ roundId: string; result: ContestResult }>;
}> {
  const byKey = new Map<
    string,
    {
      holeIdx: number;
      holeNumber: number;
      kind: ContestKind;
      label: string;
      claims: Array<{ roundId: string; result: ContestResult }>;
    }
  >();

  for (const round of rounds) {
    for (const standing of contestStandings(round, holeNumberOffset)) {
      const key = `${standing.holeIdx}:${standing.contest.kind}`;
      let entry = byKey.get(key);
      if (!entry) {
        entry = {
          holeIdx: standing.holeIdx,
          holeNumber: standing.holeNumber,
          kind: standing.contest.kind,
          label: standing.label,
          claims: [],
        };
        byKey.set(key, entry);
      }
      if (standing.result) entry.claims.push({ roundId: round.id, result: standing.result });
    }
  }

  return [...byKey.values()].sort((a, b) => a.holeIdx - b.holeIdx || a.kind.localeCompare(b.kind));
}

/**
 * Problems with a set of declarations, for the host's screen. Advisory, never blocking —
 * it is their event and unusual formats are legitimate.
 *
 * `holeNumberOffset` matters more than it looks. Hole indexes are relative to the PLAYED
 * set, so on a back nine index 0 is the hole whose sign says 10. Without the offset a
 * warning reads "Longest drive is on hole 1" while the host is staring at a tee marked 10,
 * and they will go looking for a hole that is not in their event.
 */
export function validateContests(
  contests: Record<number, HoleContest[]> | undefined,
  pars: number[],
  holeNumberOffset = 0
): string[] {
  if (!contests) return [];
  const errs: string[] = [];

  for (const key of Object.keys(contests).map(Number).sort((a, b) => a - b)) {
    const list = contests[key] ?? [];
    if (list.length === 0) continue;
    const holeNo = key + 1 + holeNumberOffset;

    if (!Number.isInteger(key) || key < 0 || key >= pars.length) {
      errs.push(`Hole ${holeNo} is not part of this round — that contest will not be seen.`);
      continue;
    }

    const seen = new Set<ContestKind>();
    for (const contest of list) {
      if (seen.has(contest.kind)) {
        errs.push(`Hole ${holeNo} has two ${CONTEST_LABELS[contest.kind]} contests on it.`);
      }
      seen.add(contest.kind);

      const par = pars[key];
      // A long drive needs a hole you can actually drive. On a par 3 you are hitting at
      // the green, so there is nothing to measure — this one is a genuine mistake, not a
      // matter of taste.
      if (contest.kind === 'longDrive' && par <= 3) {
        errs.push(`Longest drive is on hole ${holeNo}, a par ${par}. Long drives go on par 4s and 5s.`);
      }
      // Closest to the pin is traditionally a par 3, but closest-in-two on a par 5 is a
      // real contest, so this is only worth mentioning.
      if (contest.kind === 'ctp' && par >= 5) {
        errs.push(
          `Closest to the pin is on hole ${holeNo}, a par ${par} — fine if you mean closest in two, worth checking otherwise.`
        );
      }
      if (contest.eligiblePlayerIds && contest.eligiblePlayerIds.length === 0) {
        errs.push(`The contest on hole ${holeNo} has an empty eligibility list, so nobody can win it.`);
      }
    }
  }

  return errs;
}
